<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
      <TitleButton
        :showBtn="false"
        :showAddNew="false"
        title="Purchase Approval"
      />
    </div>

    <div class="px-2">
      <DateQuerySetter
        btnTitle="Go"
        @onClickGo="getPurchaseList"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-2">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label for="colFormLabel" class="col-form-label">Supplier: </label>
            <span class="filter-search-icon cursor-pointer"
                  @click="onOpenContactSearchModal">
                 <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
            placeholder="Select Supplier"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :additional-query="additionalContactOption"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-2">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label for="colFormLabel" class="col-form-label">Bill Number: </label>
          </div>
          <input
            placeholder="Bill Number"
            v-model="billNumber"
            class="form-control"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-2">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label for="colFormLabel" class="col-form-label">Memo: </label>
          </div>
          <input
              placeholder="Memo"
              v-model="memo"
              class="form-control"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="d-flex justify-content-end px-2 mt-1">
      <button
        class="btn btn-success"
        :disabled="disableApproveBtn"
        @click="handleApproval"
      >
        Approve Selected
      </button>
    </div>

    <div class="col-12 px-2 mt-1">
      <ApprovalListTable :purchase="purchaseList" @onView="onViewPurchase"/>
    </div>

    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <ContactDropdownFilter
      v-if="store.state.isModalOpenThree"
      @onSearchContact="onSearchContact"
      type="customer"
    />

    <PurchaseDetailModal
      v-if="selectedPurchaseId"
      :purchase-id="selectedPurchaseId"
      :approving="singleApproving"
      @onApprove="handleSingleApproval"
      @onClose="closeDetailModal"
    />

    <GlobalLoader />
  </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import TitleButton from '@/components/atom/TitleButton'
import Pagination from '@/components/atom/Pagination'
import ApprovalListTable from './ApprovalListTable.vue'
import PurchaseDetailModal from './PurchaseDetailModal.vue'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue"
import handleContact from "@/services/modules/contact"
import ContactDropdownFilter from "@/components/atom/Contact/ContactDropdownFilter.vue"
import { trim } from "lodash"
import { inject } from 'vue'

const router = useRouter()
const store = useStore()
const route = useRoute()
const showSuccess = inject('showSuccess')
const showError = inject('showError')

let offset = ref(20)
let purchaseList = ref([])
let contactProfile = ref(null)
let billNumber = ref(null)
let memo = ref(null)
let selectedPurchaseId = ref(null)
let singleApproving = ref(false)

const { fetchPurchaseList, approvePurchase } = handlePurchase()
const { fetchContactProfiles } = handleContact()

const companyId = computed(() => {
  return route.params.companyId
})

const additionalContactOption = ref({
  roles: 'supplier'
})

const disableApproveBtn = computed(() => {
  return purchaseList.value.filter(item => item.selected === true).length === 0
})

function getPurchaseList() {
  const query = {
    company_id: companyId.value,
    start_date: route.query.start,
    end_date: route.query.end,
    page: route.query.page,
    status: 'pending'
  }

  if (contactProfile.value) {
    query.contact_profile_id = contactProfile.value.id
  }

  if (billNumber.value && trim(billNumber.value) !== '') {
    query.bill_number = trim(billNumber.value)
  }

  if (memo.value && trim(memo.value) !== '') {
    query.description = trim(memo.value)
  }

  const queryString = new URLSearchParams(query)

  fetchPurchaseList("?" + queryString.toString())
    .then(res => {
      if (!res.status) {
        purchaseList.value = []
        store.commit("resetPagination")
        return
      }
      purchaseList.value = res.date.data
      setPagination(res.date)
    })
}

function setPagination(data) {
  if (!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

function onPageChange(page) {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  router.push({ path: route.path, query: routeQuery })
  getPurchaseList()
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

function handleApproval() {
  const purchaseIds = purchaseList.value
    .filter(item => item.selected === true)
    .map(item => item.id)

  if (!purchaseIds.length) {
    showError('Please select at least one bill')
    return
  }

  const confirmed = window.confirm('Are you sure you want to approve the selected bill(s)?')

  if (!confirmed) return

  const data = {
    company_id: companyId.value,
    purchase_ids: purchaseIds
  }

  store.state.loading = true

  approvePurchase(data)
    .then(res => {
      if (!res.status) {
        showError(res.message)
        return
      }
      showSuccess(res.message)
      getPurchaseList()
    })
    .finally(() => (store.state.loading = false))
}

function onViewPurchase(item) {
  selectedPurchaseId.value = item.id
}

function closeDetailModal() {
  selectedPurchaseId.value = null
}

function handleSingleApproval(purchaseId) {
  const confirmed = window.confirm('Are you sure you want to approve this bill?')

  if (!confirmed) return

  const data = {
    company_id: companyId.value,
    purchase_ids: [purchaseId]
  }

  singleApproving.value = true

  approvePurchase(data)
    .then(res => {
      if (!res.status) {
        showError(res.message)
        return
      }
      showSuccess(res.message)
      closeDetailModal()
      getPurchaseList()
    })
    .finally(() => (singleApproving.value = false))
}

onMounted(() => {
  getPurchaseList()
})
</script>
